import { doc, updateDoc, getDoc } from 'firebase/firestore';

/**
 * Service pour synchroniser automatiquement les données du questionnaire avec le profil du bénéficiaire
 */
export const applyProfileEnrichment = async (db, clientId, enrichmentData) => {
  if (!db || !clientId) {
    throw new Error('Base de données ou ID client manquant');
  }

  try {
    const clientRef = doc(db, 'clients', clientId);
    
    // Récupérer les données actuelles du client
    const clientDoc = await getDoc(clientRef);
    if (!clientDoc.exists()) {
      throw new Error('Client non trouvé');
    }

    const currentData = clientDoc.data();
    
    // Fusionner les données existantes avec les nouvelles données d'enrichissement
    const updatedData = {
      ...currentData,
      ...enrichmentData,
      // Mettre à jour les contacts d'urgence en fusionnant avec les existants
      emergencyContacts: mergeEmergencyContacts(
        currentData.emergencyContacts || [],
        enrichmentData.emergencyContacts || []
      ),
      // Mettre à jour le résumé de santé en fusionnant avec les existants
      healthSummary: {
        ...currentData.healthSummary,
        ...enrichmentData.healthSummary
      },
      // Mettre à jour l'évaluation d'autonomie en fusionnant avec les existants
      autonomyAssessment: {
        ...currentData.autonomyAssessment,
        ...enrichmentData.autonomyAssessment
      },
      // Mettre à jour les besoins en services en fusionnant avec les existants
      serviceNeeds: {
        ...currentData.serviceNeeds,
        ...enrichmentData.serviceNeeds
      },
      // Mettre à jour l'historique des évaluations
      assessmentHistory: updateAssessmentHistory(
        currentData.assessmentHistory || [],
        enrichmentData
      )
    };

    // Appliquer les mises à jour
    await updateDoc(clientRef, updatedData);
    
    console.log('Enrichissement du profil réussi pour le client:', clientId);
    return updatedData;
  } catch (error) {
    console.error('Erreur lors de l\'enrichissement du profil:', error);
    throw error;
  }
};

/**
 * Fusionne les contacts d'urgence existants avec les nouveaux
 * Évite les doublons basés sur le nom et le numéro principal
 */
const mergeEmergencyContacts = (existingContacts, newContacts) => {
  const mergedContacts = [...existingContacts];
  
  newContacts.forEach(newContact => {
    const existingIndex = mergedContacts.findIndex(existing => 
      existing.name === newContact.name && existing.phone1 === newContact.phone1
    );
    
    if (existingIndex === -1) {
      // Nouveau contact, l'ajouter
      mergedContacts.push(newContact);
    } else {
      // Contact existant, le mettre à jour
      mergedContacts[existingIndex] = {
        ...mergedContacts[existingIndex],
        ...newContact
      };
    }
  });
  
  return mergedContacts;
};

/**
 * Met à jour l'historique des évaluations
 */
const updateAssessmentHistory = (existingHistory, enrichmentData) => {
  const newAssessment = {
    date: new Date(),
    autonomyScore: enrichmentData.autonomyAssessment?.autonomyScore || null,
    gir: enrichmentData.autonomyAssessment?.gir || null,
    evaluator: enrichmentData.selfAssessed ? 'Auto-évaluation' : 'Évaluateur',
    observations: 'Évaluation automatique synchronisée',
    selfAssessed: enrichmentData.selfAssessed || false
  };
  
  return [newAssessment, ...existingHistory].slice(0, 10); // Garder les 10 dernières évaluations
};

/**
 * Extrait les données d'enrichissement à partir d'un questionnaire complété
 */
export const extractEnrichmentDataFromAssessment = (assessmentData, clientInfo) => {
  if (!assessmentData) return null;

  const enrichmentData = {
    emergencyContacts: [],
    healthSummary: {},
    autonomyAssessment: {},
    serviceNeeds: {},
    lastQuestionnaireUpdate: new Date(),
    questionnaireCompletionDate: new Date(),
    selfAssessed: true
  };

  // Extraire les contacts d'urgence
  if (assessmentData.contacts?.emergencyContact?.name) {
    enrichmentData.emergencyContacts.push({
      name: assessmentData.contacts.emergencyContact.name,
      relationship: assessmentData.contacts.emergencyContact.relationship || '',
      phone1: assessmentData.contacts.emergencyContact.phone1 || '',
      phone2: assessmentData.contacts.emergencyContact.phone2 || '',
      isPrimary: true
    });
  }

  // Extraire les informations de santé
  if (assessmentData.health) {
    enrichmentData.healthSummary = {
      generalHealth: assessmentData.health.generalHealth || '',
      mainPathologies: assessmentData.health.mainPathologies || '',
      medicationCount: assessmentData.health.medications?.count || null,
      fallRisk: assessmentData.health.mobility?.fallRisk || '',
      otherHealthIssues: assessmentData.health.conditions?.join(', ') || ''
    };
  }

  // Calculer le score d'autonomie
  if (assessmentData.autonomy) {
    const autonomyScore = calculateAutonomyScore(assessmentData.autonomy);
    const needsHelp = identifyNeedsHelp(assessmentData.autonomy);
    
    enrichmentData.autonomyAssessment = {
      autonomyScore,
      needsHelp,
      lastAssessmentDate: new Date()
    };
  }

  // Extraire les besoins en services
  if (assessmentData.needs) {
    enrichmentData.serviceNeeds = {
      estimatedWeeklyHours: assessmentData.needs.schedule?.weeklyHours || null,
      frequency: assessmentData.needs.schedule?.frequency || '',
      requiredServices: assessmentData.needs.services?.personalCare?.join(', ') || ''
    };
  }

  // Ajouter le GIR si disponible dans les aides financières
  if (assessmentData.currentAids?.financialAids?.apa?.gir) {
    enrichmentData.autonomyAssessment.gir = assessmentData.currentAids.financialAids.apa.gir;
  }

  return enrichmentData;
};

/**
 * Calcule un score d'autonomie basé sur les réponses du questionnaire
 */
const calculateAutonomyScore = (autonomyData) => {
  if (!autonomyData) return null;

  const categories = ['hygiene', 'elimination', 'nutrition', 'mobility', 'housekeeping', 'domestic'];
  let totalActivities = 0;
  let autonomousActivities = 0;

  categories.forEach(category => {
    if (autonomyData[category]) {
      Object.values(autonomyData[category]).forEach(value => {
        if (typeof value === 'string' && ['A', 'D', 'AP', 'AT', 'N'].includes(value)) {
          totalActivities++;
          if (value === 'A') autonomousActivities += 1;
          else if (value === 'D') autonomousActivities += 0.7;
          else if (value === 'AP') autonomousActivities += 0.4;
          else if (value === 'AT') autonomousActivities += 0.1;
        }
      });
    }
  });

  return totalActivities > 0 ? Math.round((autonomousActivities / totalActivities) * 100) : null;
};

/**
 * Identifie les domaines où le bénéficiaire a besoin d'aide
 */
const identifyNeedsHelp = (autonomyData) => {
  if (!autonomyData) return {};

  const needsHelp = { 
    hygiene: false, 
    nutrition: false, 
    mobility: false, 
    housekeeping: false, 
    domestic: false 
  };

  Object.keys(needsHelp).forEach(domain => {
    if (autonomyData[domain]) {
      const values = Object.values(autonomyData[domain]);
      needsHelp[domain] = values.some(v => ['D', 'AP', 'AT', 'N'].includes(v));
    }
  });

  return needsHelp;
};

/**
 * Vérifie si une auto-évaluation trimestrielle est nécessaire
 */
export const isQuarterlyAssessmentNeeded = (clientData) => {
  if (!clientData?.lastQuestionnaireUpdate) return true;
  
  const lastUpdate = clientData.lastQuestionnaireUpdate.toDate 
    ? clientData.lastQuestionnaireUpdate.toDate() 
    : new Date(clientData.lastQuestionnaireUpdate);
  
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  
  return lastUpdate < threeMonthsAgo;
};

/**
 * Génère un message de notification pour l'auto-évaluation trimestrielle
 */
export const getAssessmentNotificationMessage = (clientData) => {
  if (!clientData?.lastQuestionnaireUpdate) {
    return "Il est temps de compléter votre première auto-évaluation !";
  }
  
  const lastUpdate = clientData.lastQuestionnaireUpdate.toDate 
    ? clientData.lastQuestionnaireUpdate.toDate() 
    : new Date(clientData.lastQuestionnaireUpdate);
  
  const monthsSinceLastUpdate = (new Date().getMonth() - lastUpdate.getMonth() + 
    (12 * (new Date().getFullYear() - lastUpdate.getFullYear())));
  
  if (monthsSinceLastUpdate >= 3) {
    return `Votre dernière auto-évaluation remonte à ${monthsSinceLastUpdate} mois. Il est temps de la mettre à jour !`;
  }
  
  return null; // Pas de notification nécessaire
};

/**
 * Prépare les données d'enrichissement à partir des données du questionnaire
 * Compatible avec l'ancien modal d'enrichissement
 */
export const prepareProfileEnrichment = (assessmentData) => {
  if (!assessmentData) return null;

  const enrichmentData = {
    emergencyContacts: [],
    healthSummary: {},
    autonomyAssessment: {},
    serviceNeeds: {},
    housingSituation: {},
    familyCaregiver: null,
    psychologicalStatus: {},
    currentAids: {},
    lastQuestionnaireUpdate: new Date(),
    questionnaireCompletionDate: new Date()
  };

  // Extraire les contacts d'urgence
  if (assessmentData.contacts?.emergencyContact?.name) {
    enrichmentData.emergencyContacts.push({
      name: assessmentData.contacts.emergencyContact.name,
      relationship: assessmentData.contacts.emergencyContact.relationship || '',
      phone1: assessmentData.contacts.emergencyContact.phone1 || '',
      phone2: assessmentData.contacts.emergencyContact.phone2 || '',
      isPrimary: true
    });
  }

  // Personne de confiance
  if (assessmentData.contacts?.trustedPerson?.exists) {
    enrichmentData.trustedPerson = {
      name: assessmentData.contacts.trustedPerson.name || '',
      relationship: assessmentData.contacts.trustedPerson.relationship || '',
      phone: assessmentData.contacts.trustedPerson.phone || '',
      email: assessmentData.contacts.trustedPerson.email || ''
    };
  }

  // Informations de santé
  if (assessmentData.health) {
    enrichmentData.healthSummary = {
      generalHealth: assessmentData.health.generalHealth || '',
      mainPathologies: assessmentData.health.mainPathologies || '',
      medicationCount: assessmentData.health.medications?.count || null,
      fallRisk: assessmentData.health.mobility?.fallRisk || ''
    };

    // Médecin traitant
    if (assessmentData.health.professionals?.doctor?.name) {
      enrichmentData.doctor = {
        name: assessmentData.health.professionals.doctor.name,
        phone: assessmentData.health.professionals.doctor.phone || '',
        frequency: assessmentData.health.professionals.doctor.frequency || ''
      };
    }
  }

  // Évaluation d'autonomie
  if (assessmentData.autonomy) {
    const autonomyScore = calculateAutonomyScore(assessmentData.autonomy);
    const needsHelp = identifyNeedsHelp(assessmentData.autonomy);
    
    enrichmentData.autonomyAssessment = {
      autonomyScore,
      needsHelp,
      lastAssessmentDate: new Date()
    };

    // GIR si disponible
    if (assessmentData.currentAids?.financialAids?.apa?.gir) {
      enrichmentData.autonomyAssessment.gir = assessmentData.currentAids.financialAids.apa.gir;
    }
  }

  // Besoins en services
  if (assessmentData.needs) {
    enrichmentData.serviceNeeds = {
      personalCare: assessmentData.needs.services?.personalCare || [],
      housekeeping: assessmentData.needs.services?.housekeeping || [],
      accompaniment: assessmentData.needs.services?.accompaniment || [],
      estimatedWeeklyHours: assessmentData.needs.schedule?.weeklyHours || null,
      frequency: assessmentData.needs.schedule?.frequency || ''
    };
  }

  // Logement
  if (assessmentData.housing) {
    enrichmentData.housingSituation = {
      type: assessmentData.housing.type || '',
      status: assessmentData.housing.status || '',
      floor: assessmentData.housing.floor || null,
      hasElevator: assessmentData.housing.hasElevator || false,
      numberOfRooms: assessmentData.housing.numberOfRooms || null,
      adaptations: assessmentData.housing.adaptations || [],
      hasRisks: assessmentData.housing.safety?.hasRisks || false,
      risksDescription: assessmentData.housing.safety?.risksDescription || ''
    };
  }

  // Aidant familial
  if (assessmentData.project?.caregiver?.hasCaregiver) {
    enrichmentData.familyCaregiver = {
      name: assessmentData.project.caregiver.identity || '',
      relationship: assessmentData.project.caregiver.relationship || '',
      frequency: assessmentData.project.caregiver.frequency || '',
      burden: assessmentData.project.caregiver.burden || ''
    };
  }

  // Situation psychologique
  if (assessmentData.psychological) {
    enrichmentData.psychologicalStatus = {
      mood: assessmentData.psychological.mood || '',
      feelings: assessmentData.psychological.feelings || [],
      activities: assessmentData.psychological.activities || [],
      hasFollowUp: assessmentData.psychological.hasFollowUp || false,
      followUpProvider: assessmentData.psychological.followUpProvider || ''
    };
  }

  // Aides financières
  if (assessmentData.currentAids) {
    enrichmentData.currentAids = {
      apa: assessmentData.currentAids.financialAids?.apa || {},
      pch: assessmentData.currentAids.financialAids?.pch || {},
      retirement: assessmentData.currentAids.financialAids?.retirement || {},
      homeHelp: assessmentData.currentAids.homeHelp || {}
    };
  }

  return enrichmentData;
};

/**
 * Génère un résumé des données d'enrichissement disponibles
 */
export const getEnrichmentSummary = (enrichmentData) => {
  if (!enrichmentData) return {};

  return {
    contacts: (enrichmentData.emergencyContacts?.length || 0) + (enrichmentData.trustedPerson ? 1 : 0),
    health: (enrichmentData.healthSummary?.generalHealth ? 1 : 0) + (enrichmentData.doctor ? 1 : 0),
    autonomy: enrichmentData.autonomyAssessment?.autonomyScore ? 1 : 0,
    services: (enrichmentData.serviceNeeds?.personalCare?.length || 0) + (enrichmentData.serviceNeeds?.housekeeping?.length || 0),
    housing: (enrichmentData.housingSituation?.type ? 1 : 0) + (enrichmentData.housingSituation?.adaptations?.length || 0),
    caregiver: enrichmentData.familyCaregiver ? 1 : 0,
    psychological: (enrichmentData.psychologicalStatus?.mood ? 1 : 0) + (enrichmentData.psychologicalStatus?.activities?.length || 0),
    aids: (enrichmentData.currentAids?.apa?.has ? 1 : 0) + (enrichmentData.currentAids?.pch?.has ? 1 : 0)
  };
};
